import React, { useState, useEffect, useCallback, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";

import {
  deleteData,
  getAlert,
  insertData,
  updateData,
  getData,
} from "../../../../utils/SamuiUtils";

const DEFAULT_FORM = {
  DocType_Id: 0,
  DocType_Name: "",
  DocType_Status: "Y",
};

const DocTypeForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
}) => {
  const [initialFormData, setInitialFormData] = useState(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const handleBack = () => {
    window.location.href = "/docType";
  };

  useEffect(() => {
    if (mode === "I") {
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } else if (mode === "U" && selectedData) {
      const editData = {
        DocType_Id: selectedData.DocType_Id,
        DocType_Name: selectedData.DocType_Name || "",
        DocType_Status: selectedData.DocType_Status || "Y",
      };
      setFormData(editData);
      setInitialFormData(editData);
    }
  }, [mode, selectedData, setFormData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInsert = useCallback(async () => {
    try {
      const existing = await getData("Tb_Set_DocType", "DocType_Status = 'Y'");
      const newName = (formData.DocType_Name || "").trim().toLowerCase();
      const isDuplicate = Array.isArray(existing) && existing.some((r) => (r.DocType_Name || "").trim().toLowerCase() === newName);
      if (isDuplicate) {
        getAlert("FAILED", "ชื่อประเภทเอกสารนี้ถูกใช้งานอยู่แล้ว");
        return;
      }

      const { DocType_Id, ...dataToInsert } = formData;
      const fields = Object.keys(dataToInsert).join(", ");
      const values = Object.values(dataToInsert);

      await insertData("Tb_Set_DocType", fields, values);
      await callInitialize();
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } catch (error) {
      console.error("Error in handleInsert:", error);
      getAlert("FAILED", "ไม่สามารถบันทึกข้อมูลได้");
    }
  }, [formData, callInitialize, setFormData]);

  const handleUpdate = useCallback(async () => {
    try {
      const existing = await getData("Tb_Set_DocType", "DocType_Status = 'Y'");
      const newName = (formData.DocType_Name || "").trim().toLowerCase();
      const isDuplicate = Array.isArray(existing) && existing.some(
        (r) => (r.DocType_Name || "").trim().toLowerCase() === newName && r.DocType_Id !== formData.DocType_Id
      );
      if (isDuplicate) {
        getAlert("FAILED", "ชื่อประเภทเอกสารนี้ถูกใช้งานอยู่แล้ว");
        return;
      }

      const where = { DocType_Id: formData.DocType_Id };
      const { DocType_Id, ...values } = formData;
      const res = await updateData("Tb_Set_DocType", values, where);
      if (res) {
        await callInitialize();
      }
    } catch (error) {
      getAlert(
        "FAILED",
        "ไม่สามารถอัปเดตข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
    }
  }, [formData, callInitialize]);

  const handleDelete = async () => {
    try {
      getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
        if (isConfirm && selectedData?.DocType_Id !== undefined) {
          await deleteData(
            "Tb_Set_DocType",
            `WHERE DocType_Id = ${selectedData.DocType_Id}`
          );
          await callInitialize();
        }
      });
    } catch (error) {
      getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  const handleSubmit = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!formData.DocType_Name) {
      getAlert("WARNING", "โปรดกรอกชื่อประเภทเอกสาร");
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (mode === "I") {
        await handleInsert();
      } else if (mode === "U") {
        await handleUpdate();
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [formData.DocType_Name, mode, handleInsert, handleUpdate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            handleBack();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit]);

  const handleReset = () => {
    setFormData(initialFormData);
  };

  return (
    <InitialDataFormLayout
      name={name}
      entityPath="/docType"
      mode={mode}
      onSubmit={handleSubmit}
      isSubmittingDisabled={isSaving}
      onBack={handleBack}
      onReset={handleReset}
      onDelete={mode === "U" ? handleDelete : undefined}
      callInitialize={callInitialize}
    >
      <div style={sectionHeaderStyle}>
        <i className="fas fa-file-alt" style={sectionIconStyle}></i>
        <h5 style={sectionTitleStyle}>ประเภทเอกสาร</h5>
      </div>
      <div className="row g-4">
        <div className="col-md-6">
          <label style={labelStyle}>ชื่อประเภทเอกสาร</label>
          <input
            type="text"
            className="form-control"
            name="DocType_Name"
            value={formData.DocType_Name || ""}
            onChange={handleChange}
            style={inputStyle}
          />
        </div>
        <div className="col-md-6">
          <label style={labelStyle}>สถานะ</label>
          <select
            className="form-select"
            name="DocType_Status"
            value={formData.DocType_Status || "Y"}
            onChange={handleChange}
            style={selectStyle}
          >
            <option value="Y">ใช้งาน</option>
            <option value="N">ไม่ใช้งาน</option>
          </select>
        </div>
      </div>
    </InitialDataFormLayout>
  );
};

export default DocTypeForm;
